import { Component, OnDestroy, OnInit } from '@angular/core';
import { Announcement } from '../../../../core/models/public.models';
import { ProductCardView } from '../../../../core/models/product-api.models';
import { AgencyCardView } from '../../../../core/models/agency-api.models';
import { PublicDataService } from '../../../../core/services/public-data.service';
import { ProductApiService } from '../../../../core/services/product-api.service';
import { AgencyApiService } from '../../../../core/services/agency-api.service';
import { ImageApiService } from '../../../../core/services/image-api.service';
import { CharterColorService } from '../../../../core/services/charter-color.service';
import { Subject, takeUntil } from 'rxjs';

interface QuickAction {
  icon: string;
  title: string;
  description: string;
  route: string;
  fragment?: string;
  color: string;
  gradient: string;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {
  announcements: Announcement[] = [];
  products: ProductCardView[] = [];
  agencies: AgencyCardView[] = [];

  announcementsLoading = true;
  readonly productSkeletons = [0, 1, 2, 3];
  productsLoading = true;
  productsError: string | null = null;
  agenciesLoading = false;
  agenciesError: string | null = null;

  activeAnnouncementIndex = 0;
  private carouselTimer: ReturnType<typeof setInterval> | null = null;
  private readonly destroy$ = new Subject<void>();

  quickActions: QuickAction[] = [];

  constructor(
    private readonly publicData: PublicDataService,
    private readonly productApi: ProductApiService,
    private readonly agencyApi: AgencyApiService,
    private readonly imageApi: ImageApiService,
    private readonly charterColor: CharterColorService,
  ) {}

  ngOnInit(): void {
    this.initQuickActions();
    this.loadCarousel();
    this.loadProducts();
    this.loadAgencies();
  }

  ngOnDestroy(): void {
    this.stopCarousel();
    this.destroy$.next();
    this.destroy$.complete();
  }

  reloadProducts(): void {
    this.loadProducts(true);
  }

  reloadAgencies(): void {
    this.loadAgencies(true);
  }

  prevAnnouncement(): void {
    const len = this.announcements.length || 1;
    this.activeAnnouncementIndex = (this.activeAnnouncementIndex - 1 + len) % len;
    this.resetCarousel();
  }

  nextAnnouncement(): void {
    const len = this.announcements.length || 1;
    this.activeAnnouncementIndex = (this.activeAnnouncementIndex + 1) % len;
    this.resetCarousel();
  }

  selectAnnouncement(idx: number): void {
    this.activeAnnouncementIndex = idx;
    this.resetCarousel();
  }

  onProductImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/products/product-auto.jpg';
  }

  trackByProductId(_: number, product: ProductCardView): string {
    return product.id;
  }

  trackByActionTitle(_: number, action: QuickAction): string {
    return action.title;
  }

  trackByAgencyId(_: number, agency: AgencyCardView): string {
    return agency.id;
  }

  private loadCarousel(): void {
    this.announcementsLoading = true;
    this.imageApi
      .getCarouselAnnouncements()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (slides) => {
          this.applyAnnouncements(slides.length ? slides : this.publicData.getAnnouncements());
        },
        error: (err) => {
          console.error('[Home] carousel images load failed', err);
          this.applyAnnouncements(this.publicData.getAnnouncements());
        },
      });
  }

  private applyAnnouncements(slides: Announcement[]): void {
    this.announcements = slides;
    this.activeAnnouncementIndex = 0;
    this.announcementsLoading = false;
    this.resetCarousel();
  }

  private loadProducts(forceRefresh = false): void {
    this.productsLoading = true;
    this.productsError = null;

    this.productApi
      .listCards(forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (products) => {
          this.products = products;
          this.productsLoading = false;
        },
        error: (err) => {
          console.error('[Home] products load failed', err);
          this.products = [];
          this.productsLoading = false;
          this.productsError = 'Impossible de charger les produits. Réessayez plus tard.';
        },
      });
  }

  private loadAgencies(forceRefresh = false): void {
    this.agenciesLoading = true;
    this.agenciesError = null;

    this.agencyApi
      .listCards(forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agencies) => {
          this.agencies = agencies;
          this.agenciesLoading = false;
        },
        error: (err) => {
          console.error('[Home] agencies load failed', err);
          this.agencies = [];
          this.agenciesLoading = false;
          this.agenciesError = 'Impossible de charger les agences. Réessayez plus tard.';
        },
      });
  }

  private startCarousel(): void {
    if (this.announcements.length > 1) {
      this.carouselTimer = setInterval(() => this.nextAnnouncement(), 6500);
    }
  }

  private stopCarousel(): void {
    if (this.carouselTimer) {
      clearInterval(this.carouselTimer);
      this.carouselTimer = null;
    }
  }

  private resetCarousel(): void {
    this.stopCarousel();
    this.startCarousel();
  }

  private initQuickActions(): void {
    const palette = this.charterColor.getAccentPalette();
    const templates: Omit<QuickAction, 'color' | 'gradient'>[] = [
      {
        icon: 'shopping_cart',
        title: 'Souscrire à une police',
        description: 'Choisissez votre couverture et souscrivez en quelques minutes.',
        route: '/devis',
      },
      {
        icon: 'credit_card',
        title: 'Payer ma prime',
        description: "Réglez vos primes d'assurance en toute sécurité.",
        route: '/se-connecter',
      },
      {
        icon: 'report_problem',
        title: 'Déclarer un sinistre',
        description: 'Signalez un incident et suivez votre dossier en temps réel.',
        route: '/nous-contacter',
      },
      {
        icon: 'help_outline',
        title: "Besoin d'aide",
        description: 'Contactez notre support ou consultez notre FAQ.',
        route: '/nous-contacter',
      },
      {
        icon: 'location_on',
        title: 'Voir nos agences',
        description: "Trouvez l'agence Saarvie la plus proche de chez vous.",
        route: '/accueil',
        fragment: 'agences',
      },
      {
        icon: 'calculate',
        title: 'Faire une simulation',
        description: 'Estimez le coût de votre assurance avant de souscrire.',
        route: '/devis',
      },
    ];

    this.quickActions = templates.map((action, index) => {
      const color = palette[index % palette.length];
      const next = palette[(index + 1) % palette.length];
      return {
        ...action,
        color,
        gradient: `linear-gradient(135deg, ${color}, ${next})`,
      };
    });
  }
}
